import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, TrendingUp, ChevronRight, PlusCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { bookingService } from '../../services/bookingService';
import { UI, buttonStyle, ui } from '../../utils/ui';

const statusPill = (status) => {
  const map = {
    confirmed: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' },
    pending: { backgroundColor: '#fef9c3', color: '#854d0e', borderColor: '#fde047' },
    completed: { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' },
    cancelled: { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' },
  };
  const c = map[status] || map.pending;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 800,
    border: `1px solid ${c.borderColor}`,
    backgroundColor: c.backgroundColor,
    color: c.color,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  };
};

const CustomerDashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bookingService.getMyBookings()
      .then((res) => setBookings(res.data.bookings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split('T')[0];

  const { upcoming, completed } = useMemo(() => {
    const u = bookings.filter((b) => b.check_in >= today && ['confirmed', 'pending'].includes(b.booking_status));
    const c = bookings.filter((b) => b.booking_status === 'completed');
    return { upcoming: u, completed: c };
  }, [bookings, today]);

  const stats = [
    { icon: <Calendar size={22} />, label: 'Total Bookings', value: bookings.length, accent: UI.colors.primary, bg: 'rgba(22,163,74,0.12)' },
    { icon: <Clock size={22} />, label: 'Upcoming Stays', value: upcoming.length, accent: UI.colors.info, bg: 'rgba(37,99,235,0.12)' },
    { icon: <TrendingUp size={22} />, label: 'Completed Stays', value: completed.length, accent: UI.colors.warning, bg: 'rgba(245,158,11,0.14)' },
    { icon: <User size={22} />, label: 'Account Type', value: 'Customer', accent: '#7c3aed', bg: 'rgba(124,58,237,0.12)' },
  ];

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 22 };
  const headerStyle = { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 };
  const titleStyle = { ...ui.h1, fontSize: 30 };
  const subtitleStyle = { ...ui.subtitle, marginTop: 6 };
  const primaryLinkStyle = { ...buttonStyle('primary'), padding: '10px 14px', borderRadius: 14 };
  const cardPadStyle = { padding: 20 };
  const sectionTitleStyle = { fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, color: UI.colors.text, margin: 0 };
  const textLinkStyle = { fontSize: 13, fontWeight: 900, color: UI.colors.primary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const sortedRecent = [...bookings].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 3);

  return (
    <div style={wrapStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>Dashboard</h1>
          <p style={subtitleStyle}>
            Welcome back, <span style={{ color: UI.colors.primary, fontWeight: 900 }}>{user?.name}</span>!
          </p>
        </div>
        <Link to="/customer/book-room" style={primaryLinkStyle}>
          <PlusCircle size={18} /> Book New Room
        </Link>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        {stats.map((s) => (
          <div key={s.label} style={{ ...ui.card, ...cardPadStyle }}>
            <div style={{ width: 46, height: 46, borderRadius: 14, backgroundColor: s.bg, color: s.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              {s.icon}
            </div>
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</p>
            <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 26, fontWeight: 900, color: UI.colors.text }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
        {/* Recent Bookings */}
        <div style={{ ...ui.card }}>
          <div style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              <h2 style={sectionTitleStyle}>Recent Bookings</h2>
              <Link to="/customer/bookings" style={textLinkStyle}>
                View All <ChevronRight size={16} />
              </Link>
            </div>

            {bookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '26px 10px', color: UI.colors.muted }}>
                <Calendar size={40} color="#9ca3af" style={{ opacity: 0.35, margin: '0 auto 10px' }} />
                <p style={{ margin: '0 0 12px', fontWeight: 800 }}>No bookings yet.</p>
                <Link to="/customer/book-room" style={{ ...buttonStyle('primary'), padding: '10px 14px', borderRadius: 14 }}>
                  Book Your First Room
                </Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sortedRecent.map((b) => (
                  <div key={b.id} style={{ border: `1px solid ${UI.colors.border}`, borderRadius: 16, padding: 14, backgroundColor: '#ffffff' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 900, color: UI.colors.text }}>{b.room_name}</p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>#{b.id}</p>
                      </div>
                      <span style={statusPill(b.booking_status)}>{b.booking_status}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
                      {[
                        ['Check-in', b.check_in],
                        ['Check-out', b.check_out],
                        ['Guests', b.guests],
                        ['Amount', `₹${Number(b.total_amount).toLocaleString()}`],
                      ].map(([lbl, val]) => (
                        <div key={lbl}>
                          <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{lbl}</p>
                          <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: 900, color: UI.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Quick Actions */}
          <div style={ui.card}>
            <div style={{ padding: 22 }}>
              <h2 style={{ ...sectionTitleStyle, marginBottom: 12 }}>Quick Actions</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { to: '/customer/book-room', label: 'New Booking' },
                  { to: '/customer/bookings', label: 'My Bookings' },
                  { to: '/customer/profile', label: 'Edit Profile' },
                ].map(({ to, label }) => (
                  <Link key={to} to={to} style={{ ...buttonStyle('outline'), width: '100%', justifyContent: 'flex-start', padding: '10px 12px', borderRadius: 14 }}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Next Stay */}
          {upcoming.length > 0 && (
            <div
              style={{
                borderRadius: UI.radius.lg,
                padding: 22,
                background: 'linear-gradient(135deg, rgba(22,163,74,1) 0%, rgba(21,128,61,1) 100%)',
                color: '#ffffff',
                boxShadow: UI.shadow.sm,
              }}
            >
              <h2 style={{ fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, margin: 0, opacity: 0.92 }}>Next Stay</h2>
              <p style={{ margin: '10px 0 0', fontSize: 16, fontWeight: 900 }}>{upcoming[0].room_name}</p>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6, opacity: 0.9, fontSize: 13 }}>
                <p style={{ margin: 0 }}>Check-in: {upcoming[0].check_in}</p>
                <p style={{ margin: 0 }}>Check-out: {upcoming[0].check_out}</p>
              </div>
              <span style={{ display: 'inline-flex', marginTop: 12, padding: '4px 10px', borderRadius: 9999, border: '1px solid rgba(255,255,255,0.25)', backgroundColor: 'rgba(255,255,255,0.16)', fontSize: 11, fontWeight: 900, textTransform: 'capitalize' }}>
                {upcoming[0].booking_status}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;

